import Immutable, { fromJS } from "immutable"
import moment from "moment"
import { createSelector, createStructuredSelector } from "reselect"
import isEqual from "lodash.isequal"

import momentTimezone from "moment-timezone"

import { createToJsSelector, getAllMatches, createExtraFieldSelector } from "shared/imports/sharedHelperFunctions"
import { PREVIEWABLE_FIELDS } from "shared/profiles/file/constants"
import {
    outboxConstants,
    outboxFinalizeConstants,
    validationContants,
    AB_TEST_SEND_MINIMUM,
} from "shared/outbox/constants"
import {
    selectSpecificCustomDescription,
    makeSelectFieldFromCustomDescription,
} from "shared/state_management/selectors"

import {
    linksAndContentRegex,
    multipleSpacesRegex,
    grabInsideStyle,
    slashInsidePlaceholder,
    spaceInsidePlaceholder,
    htmlQuoteRegex,
    emailValidationRegex,
} from "shared/imports/regex"
import { DEFAULT_QUORUM_DOMAIN, OLD_DEFAULT_QUORUM_DOMAIN } from "shared/imports/sharedConstants"
import { isFeatureEnabled } from "shared/featureflags/helperFunctions"
import { checkPermission } from "shared/imports/permissionFunctions"
import { makeUserdataSelector, selectIsLimitedToTeamsEnabled } from "shared/userdata/userdata-selectors"

import blankBeeFree from "shared/outbox/blankBeeFree"
import { EmailPreviewStatusCodes } from "shared/outbox/action-types"
import { validateHtmlContent, containsImagesOnly, containsPlaceholdersOnly } from "shared/outbox/helpers"

const { EmailStatus } = DjangIO.app.userdata.outbox.types

const getOutbox = (state) => state.outbox

export const getBulkEmailId = createSelector(getOutbox, (outbox) => outbox.get("bulkEmailId"))

export const selectBulkEmails = createSelector(getOutbox, (outbox) => outbox.get("bulkEmails"))

/**
 * Creates a simple selectors for bulk_email
 *
 * @param {String} key string of key to get
 * @param {String} defaultValue value to return if the key *does not exist*
 * @param {String} bulkEmailId Allows selecting from any BulkEmail already loaded in the store, not just the active one
 * @returns {Function} returns the select function
 */
export const createBulkEmailSelector = (key, defaultValue, bulkEmailId) =>
    createSelector(getOutbox, getBulkEmailId, (outbox, activeBulkEmailId) => {
        const value =
            (bulkEmailId || activeBulkEmailId) &&
            outbox &&
            outbox.getIn &&
            outbox.getIn(["bulkEmails", String(bulkEmailId || activeBulkEmailId), key], defaultValue)

        return typeof value !== "undefined" ? value : defaultValue
    })

/*
 * HEADER SELECTORS
 */
export const getId = createBulkEmailSelector("id")

export const contentVersion = createBulkEmailSelector("content_version")

// moment of the last update to the compose form
export const getLastUpdatedMoment = createBulkEmailSelector("lastUpdated")

// moment of the last successful autosave of the compose form
export const getLastAutosavedMoment = createBulkEmailSelector("lastAutosaved")

export const creator = createExtraFieldSelector({
    objectSelector: createBulkEmailSelector,
    key: "creator",
})

export const truncationData = createExtraFieldSelector({
    objectSelector: createBulkEmailSelector,
    key: "truncation_data",
})

export const selectIsTruncated = createExtraFieldSelector({
    objectSelector: createBulkEmailSelector,
    key: "truncation_data",
})

export const selectDetailOptions = createSelector(selectIsTruncated, (truncation_data) =>
    DjangIO.app.userdata.outbox.types.EmailReport.items()
        .filter((opt) => opt.show_in_detail_page)
        .filter(
            (option) =>
                !(
                    truncation_data &&
                    truncation_data.get("is_truncated_messageevent") &&
                    option.value === DjangIO.app.userdata.outbox.types.EmailReport.all_message_events.value
                ),
        ),
)

export const selectIsSendEmailTruncated = createSelector(
    selectIsTruncated,
    (truncation_data) => truncation_data && truncation_data.get("is_truncated_sendemail"),
)

// from users
export const fromUser = createBulkEmailSelector("from_user")
const fromIdResultFunc = (fromId) =>
    fromId ? parseInt(DjangIO.app.userdata.models.QuorumUser.idFromResourceUri(fromId)) : null
export const fromUserId = createSelector(fromUser, fromIdResultFunc)
export const fromName = createBulkEmailSelector("from_name")

export const fromAddressResultFunc = (fromEmailAddress) =>
    fromEmailAddress && fromEmailAddress.includes("@") ? fromEmailAddress.split("@")[0] : undefined

// Select the email username ex: [devon]@quorum.us
export const fromAddress = createSelector(createBulkEmailSelector("from_address"), fromAddressResultFunc)

const fromDomainResultFunc = (fromEmailAddress) => {
    if (fromEmailAddress && fromEmailAddress.includes("@")) {
        const domain = fromEmailAddress.split("@")[1]

        // If the domain is not a valid sending domain
        if (!Userdata.outbox_domains.includes(domain)) {
            // Get the default quorum domain
            return Userdata.outbox_domains.find((val) => val.includes(outboxConstants.DEFAULT_QUORUM_DOMAIN))
        }

        return domain
    }

    return undefined
}

// Select the email domain ex: devon@[quorum.us]
export const fromDomain = createSelector(createBulkEmailSelector("from_address"), fromDomainResultFunc)

// Select the combined email username and reconciled domain ex: "devon" + "@" + "quorum.us"
export const fromFullEmailAddress = createSelector(
    createBulkEmailSelector("from_address"),
    fromAddress,
    fromDomain,
    (fromEmailAddress, address, domain) =>
        fromEmailAddress && fromEmailAddress.includes("@") ? `${address}@${domain}` : "",
)

// This creates a nice formatted thing for the from name
export const formattedFromName = createSelector(
    [fromName, fromFullEmailAddress],
    (name, address) => `${name || ""} <${address}>`,
)

/**
 * Get names of EmailDomains that are verified and can be used for sending outbox emails
 *
 * @returns {Array} array of string names of verified email domains
 */
export const validFromDomains = () =>
    Object.keys(Userdata.outbox_domain_dict_detailed).filter(
        (domain) => Userdata.outbox_domain_dict_detailed[domain].is_domain_verified,
    )

const makeFromEmailResultFunc = (fromAddr, fromDom) => {
    // edge cases for users that cannot send from any outbox
    // TODO abtesting: handle AB testing case; we should only check if
    // the original email author has this permission for AB tests
    if (!Userdata.Permissions.can_send_from_any_outbox) {
        // get the email and valid domains from Userdata
        const { email, outbox_domains } = Userdata
        const [address, domain] = email.split("@")
        // if the valid domains don't include the user's email domain
        if (!outbox_domains.includes(domain)) {
            // get the quorum domain
            const quorumDomain = outbox_domains.find((val) => val.includes(DEFAULT_QUORUM_DOMAIN))
            return `${address}@${quorumDomain}`
        }
        return `${fromAddr}@${fromDom}`
    }

    return fromDom in Userdata.outbox_domain_dict ? `${fromAddr}@${fromDom}` : `${fromAddr}@${Userdata.default_domain}`
}

export const makeFromEmail = createSelector([fromAddress, fromDomain], makeFromEmailResultFunc)

// reply to things
export const replyToUser = createBulkEmailSelector("reply_to_user")
export const replyToUserId = createSelector(replyToUser, (replyToId) =>
    replyToId ? parseInt(DjangIO.app.userdata.models.QuorumUser.idFromResourceUri(replyToId)) : null,
)
// this grabs the reply to name
export const replyToName = createBulkEmailSelector("reply_to_name")

// Select the email username ex: [devon]@quorum.us
export const replyToAddress = createSelector(createBulkEmailSelector("reply_to_address"), (replyToEmailAddress) =>
    replyToEmailAddress && replyToEmailAddress.includes("@") ? replyToEmailAddress.split("@")[0] : undefined,
)

// Select the email domain ex: devon@[quorum.us]
export const replyToDomain = createSelector(createBulkEmailSelector("reply_to_address"), (replyToEmailAddress) =>
    replyToEmailAddress && replyToEmailAddress.includes("@") ? replyToEmailAddress.split("@")[1] : undefined,
)

// Select the combined email username and reconciled domain ex: "devon" + "@" + "quorum.us"
export const replyToFullEmailAddress = createSelector(
    createBulkEmailSelector("reply_to_address"),
    replyToAddress,
    replyToDomain,
    (replyToEmailAddress, replyAddr, replyDomain) =>
        replyToEmailAddress && replyToEmailAddress.includes("@") ? `${replyAddr}@${replyDomain}` : "",
)

// formatted name for detail page
export const formattedReplyToName = createSelector(
    [replyToName, replyToFullEmailAddress],
    (name, address) => `${name || ""} <${address}>`,
)

// gets Projects
const getImmutableProjects = createBulkEmailSelector("projects", [])
// gets the uris
export const projectUris = createSelector(getImmutableProjects, (projectList) => {
    const newList = []

    // update the list
    projectList.forEach((p) => newList.push(p))
    return newList
})

/*
 * SENDING INFORMATION
 */

// const created = createBulkEmailSelector("created")  // created: "2017-03-27T11:38:11.575693"
export const isDraft = createBulkEmailSelector("is_draft", true)
export const currentStatus = createBulkEmailSelector("current_status")
export const unsubscribeEnabled = createBulkEmailSelector("unsubscribe_enabled", false)
export const isCustomUnsubscribe = createBulkEmailSelector("is_custom_unsubscribe", false)
export const orgUnsubLinkText = makeUserdataSelector("organization_unsubscribe_link_text", "")
export const defaultUnsubscribeSetting = makeUserdataSelector("organization_default_unsubscribe_setting")
export const isDefaultQuorumDomain = createSelector(
    fromDomain,
    (fromDomain) =>
        fromDomain && (fromDomain.includes(DEFAULT_QUORUM_DOMAIN) || fromDomain.includes(OLD_DEFAULT_QUORUM_DOMAIN)),
)
export const unsubRadioChecked = createSelector(
    unsubscribeEnabled,
    isCustomUnsubscribe,
    orgUnsubLinkText,
    isDefaultQuorumDomain,
    (unsubscribeEnabled, isCustomUnsubscribe, orgUnsubLinkText, isDefaultQuorumDomain) => {
        /*
            Due to Google and Yahoo's email sender guidelines, we want to always default to showing the unsubscribe link
        */
        const showPreconfiguredUnsubscribeLink = DjangIO.app.userdata.types.UnsubscribeSettings.preconfigured.value
        const showCustomizedUnsubscribeLink = DjangIO.app.userdata.types.UnsubscribeSettings.customized.value
        const dontShowUnsubscribeLink = DjangIO.app.userdata.types.UnsubscribeSettings.dont_show.value

        if (isDefaultQuorumDomain) {
            return orgUnsubLinkText.length && isCustomUnsubscribe
                ? showCustomizedUnsubscribeLink
                : showPreconfiguredUnsubscribeLink
        }
        if (orgUnsubLinkText.length) {
            return unsubscribeEnabled
                ? isCustomUnsubscribe
                    ? showCustomizedUnsubscribeLink
                    : showPreconfiguredUnsubscribeLink
                : dontShowUnsubscribeLink
        }
        return unsubscribeEnabled ? showPreconfiguredUnsubscribeLink : dontShowUnsubscribeLink
    },
)
export const isScheduled = createBulkEmailSelector("is_scheduled", false)
export const scheduledSendTime = createBulkEmailSelector("scheduled_send_time", moment())
export const scheduledSendMoment = createSelector(scheduledSendTime, moment)
export const usePersonalEmail = createBulkEmailSelector("use_personal_email")
export const shouldCcAll = createBulkEmailSelector("should_cc_all", false)

export const sendFromAssignments = createBulkEmailSelector("send_from_assignments")
export const replyToAssignments = createBulkEmailSelector("reply_to_assignments")

export const selectFromEmailInSwal = createSelector(
    makeFromEmail,
    sendFromAssignments,
    (fromEmail, sendFromAssignments) => (sendFromAssignments ? "the assigned senders" : fromEmail),
)

export const selectFromNameInSwal = createSelector(fromName, sendFromAssignments, (fromName, sendFromAssignments) =>
    sendFromAssignments ? "from Assigned Senders" : `as ${fromName}`,
)

/*
 * RECIPIENT INFORMATION
 */

// selection
const getSelectionJson = createBulkEmailSelector("selection_json", fromJS({}))
const selectionJson = createToJsSelector(getSelectionJson)
export const selection = createSelector(selectionJson, ({ data }) => data || [])

/* check if the personal email toggles should be rendered depending on whether
    there is a selection with officials in it.
    We cast the two values to a boolean so that nothing (instead of 0 or the obj)
    gets rendered as the component.
*/
export const shouldRenderPersonalToggles = createSelector(
    selection,
    (selectionData) =>
        !!selectionData.length &&
        !!selectionData.find(
            (dataObj) =>
                dataObj.quorumDataType === DjangIO.app.models.QuorumDataType.person.value ||
                dataObj.model === DjangIO.app.models.QuorumDataType.person.label ||
                dataObj.quorumDataType === DjangIO.app.models.QuorumDataType.staffer.value ||
                dataObj.model === DjangIO.app.models.QuorumDataType.staffer.label,
        ),
)
// exclusion
const getExcludesJson = createBulkEmailSelector("excludes_json", fromJS({}))
const excludesJson = createToJsSelector(getExcludesJson)
export const excludes = createSelector(excludesJson, ({ data }) => data || [])

// Counting stats
export const emailCount = createBulkEmailSelector("email_count", 0)
export const personCount = createBulkEmailSelector("person_count")
export const supporterCount = createBulkEmailSelector("supporter_count")
export const selectPressCount = createBulkEmailSelector("press_count")
export const excludedEmailCount = createBulkEmailSelector("excluded_email_count", 0)
export const excludedPersonCount = createBulkEmailSelector("excluded_person_count")
export const excludedSupporterCount = createBulkEmailSelector("excluded_supporter_count")
export const selectExcludedPressContactCount = createBulkEmailSelector("excluded_press_contact_count")

export const selectHasOfficials = createSelector(selection, (selectionData) =>
    selectionData.find((dataObj) =>
        // QDTs that map to the "Officials" dataset on the frontend
        [
            DjangIO.app.models.QuorumDataType.person.value,
            DjangIO.app.models.QuorumDataType.member.value,
            DjangIO.app.models.QuorumDataType.executive.value,
            DjangIO.app.models.QuorumDataType.local_member.value,
            DjangIO.app.models.QuorumDataType.legislator.value,
        ].includes(dataObj.quorumDataType),
    ),
)
export const selectHasStaffers = createSelector(selection, (selectionData) =>
    selectionData.find((dataObj) => dataObj.quorumDataType === DjangIO.app.models.QuorumDataType.staffer.value),
)
export const selectHasPressContacts = createSelector(selection, (selectionData) =>
    selectionData.find((dataObj) => dataObj.quorumDataType === DjangIO.app.models.QuorumDataType.press_contact.value),
)

export const selectIsOverPressLimit = createSelector(
    selectPressCount,
    (pressCount) => pressCount > outboxConstants.PRESS_CONTACT_LIMIT,
)

export const isOverRecipientUnsubscribeLimit = createSelector(
    emailCount,
    (emailCount) => emailCount > outboxConstants.RECIPIENT_UNSUBSCRIBE_THRESHOLD,
)

// The Bccs
const getImmutableBccs = createBulkEmailSelector("bcc_rcpts", fromJS([]))
export const bccRcpts = createSelector(getImmutableBccs, (bccs) => bccs && bccs.toJS().filter(Boolean))

// The Ccs
const getImmutableCcs = createBulkEmailSelector("cc_rcpts", fromJS([]))
export const ccRcpts = createSelector(getImmutableCcs, (ccs) => ccs.toJS().filter(Boolean))

// External ID
export const externalId = createBulkEmailSelector("external_id")

/**
 * AB Testing Selectors
 */
export const selectIsABTest = createBulkEmailSelector("is_abtest", false)

export const selectABTestVariantIds = createSelector(createBulkEmailSelector("variants", fromJS([])), (variants) =>
    variants.map((variantUri) => DjangIO.app.userdata.outbox.models.bulk_email.BulkEmail.idFromResourceUri(variantUri)),
)

export const selectABTestVariants = createSelector(
    selectBulkEmails,
    selectABTestVariantIds,
    (bulkEmails, variantIds) =>
        bulkEmails
            .valueSeq()
            .sortBy((bulkEmail) => bulkEmail.id)
            .filter((bulkEmail) => variantIds.includes(bulkEmail.get("id"))) || fromJS([]),
)

export const selectABTest = createBulkEmailSelector("abtest", fromJS({}))

/**
 * Creates a simple selector for AB Tests
 *
 * @param {String} key string of key to get
 * @param {any} defaultValue Value provided if the key is not found
 * @returns {Function} returns the select function
 */
export const createABTestSelector = (key, defaultValue) =>
    createSelector(selectABTest, (abTest) =>
        abTest && typeof abTest.get(key) !== "undefined" ? abTest.get(key) : defaultValue,
    )

export const selectABTestWinnerId = createABTestSelector("winner_id")
export const selectABTestWinner = createSelector(selectBulkEmails, selectABTestWinnerId, (bulkEmails, winnerId) =>
    bulkEmails.valueSeq().find((bulkEmail) => winnerId === bulkEmail.get("id")),
)

/*
 * CONTENT
 */
export const makeGetSubject = (variantId) => createBulkEmailSelector("subject", "", variantId)
export const getSubject = makeGetSubject()
export const subject = createSelector(getSubject, (subjectString) => subjectString)

export const getABTestedSubject = (variantId) => makeGetSubject(variantId)

export const composeTool = createBulkEmailSelector("compose_tool", "summernote")
export const htmlContent = createSelector(
    createBulkEmailSelector("html_content", ""),
    // we need to get rid of all the '&quot;' characters in the html content
    // because of the way summernote parses fonts. If we don't replace these,
    // multi-word font names like "Times New Roman" will be parsed incorrectly
    // and default to Helvetica Neue
    (uncleanHtmlContent) => uncleanHtmlContent.replace(htmlQuoteRegex, "'"),
)

// bee plugin
const getBeeImmutable = createBulkEmailSelector("beePluginInstance")
export const getBeePlugin = createToJsSelector(getBeeImmutable)

const selectBeeImmutable = (bulkEmailId) => createBulkEmailSelector("beePluginInstance", undefined, bulkEmailId)
export const selectBeePlugin = (bulkEmailId) => createToJsSelector(selectBeeImmutable(bulkEmailId))

// the bee json
const getImmutableJsonContent = createBulkEmailSelector("json_content")
export const getJsonContent = createToJsSelector(getImmutableJsonContent)
export const getAiSuggestion = createToJsSelector(createBulkEmailSelector("ai_suggestion"))

// the attachments
const getImmutableAttachments = createBulkEmailSelector("attachments", [])
// export const getAttachments = createToJsSelector(getImmutableAttachments)
export const getAttachments = createSelector(getImmutableAttachments, (attachmentList) => {
    const attachmentObjs = []
    attachmentList.forEach((a) => {
        attachmentObjs.push({
            id: a.get("id"),
            name: a.get("name"),
            size: a.get("size"),
            mimeType: a.get("mime_type"),
            base64Data: a.get("content"),
            canPreview: PREVIEWABLE_FIELDS.some(
                (currRegex) => a.get("mime_type") && currRegex.test(a.get("mime_type").toLowerCase()),
            ),
        })
    })
    return attachmentObjs
})

export const getAttachmentsForMobileDataItem = createSelector(getImmutableAttachments, (attachmentList) => {
    const attachmentObjs = []
    attachmentList.forEach((a) => {
        attachmentObjs.push({
            id: a.get("id"),
            name: a.get("name"),
        })
    })
    return attachmentObjs
})

// the attachment uris
const attachmentUris = createSelector(getAttachments, (attachmentList) =>
    attachmentList.map((a) => DjangIO.app.userdata.models.Attachment.foreignKey(a.id)),
)

const selectBulkEmailLimitedToTeams = createSelector(
    createBulkEmailSelector("limited_to_teams"),
    (limitedToTeams) => limitedToTeams || Immutable.fromJS([]),
)

export const selectLimitedToTeamsForBulkEmailForm = createSelector(
    selectBulkEmailLimitedToTeams,
    (limitedToTeamsList) => limitedToTeamsList.toJS(),
)

export const selectShouldShowTeamPermissioning = createSelector(
    selectIsLimitedToTeamsEnabled,
    makeUserdataSelector("should_hide_team_permissionining_in_forms", false),
    (limitedToTeamsEnabled, shouldHideTeamPermissioning) => limitedToTeamsEnabled && !shouldHideTeamPermissioning,
)
/*
 * Placeholders and previewStatus
 */

// preview controls
const gettingPreviews = createBulkEmailSelector("gettingPreviews", false)
export const getPreviewIndex = createBulkEmailSelector("previewIndex", 0)
export const getEmailPlaceholderStatus = createBulkEmailSelector(
    "emailPlaceholderStatus",
    EmailPreviewStatusCodes.READY,
)

/**
 * The 'SearchableDropdownButton' expects placeholders with 'label' denoting the names of the placeholders,
 * and 'options' for each individual placeholder item.
 * This parses the given placeholders to format for use in 'SearchableDropdownButton' component
 *
 * @param {list} placeholders list of placeholder groups
 * @returns {list} list of parsed placeholder groups with 'label' and 'options'
 */
export const parsePlaceholdersForSearchableDropdownButton = (placeholders) =>
    placeholders &&
    placeholders.map((item) => ({
        ...item,
        label: item.name,
        value: item.value,
        options: item.items.map((subItem) => ({
            ...subItem,
            label: subItem.name,
            value: subItem.value,
        })),
    }))

// gets the placeholder array
const getImmutablePlaceholders = createBulkEmailSelector("placeholders", fromJS([]))
export const getPlaceholders = createSelector(createToJsSelector(getImmutablePlaceholders), (placeholders) =>
    parsePlaceholdersForSearchableDropdownButton(placeholders),
)

// gets the preview objects
const getImmutablePreviews = createBulkEmailSelector("previews", fromJS([]))
export const getPreviews = createToJsSelector(getImmutablePreviews)

// gets the array of invalid placeholders
const getImmutableInvalidPlaceholders = createBulkEmailSelector("invalidPlaceholders")
export const getInvalidPlaceholders = createToJsSelector(getImmutableInvalidPlaceholders)

const emptyPreviewCheckerFunc = (previews) =>
    previews.length === 0 ||
    previews[0].body
        .replace(/(\n|\s)/g, "")
        .replace(/<(style|head)(.|\n)*?<\/(style|head)>/gim, "")
        .replace(/<\/?[^>]+(>|$)/gim, "")
        .replace(/&.*?;/gim, "") === ""

// if the previews are empty
const hasEmptyPreview = createSelector(getPreviews, emptyPreviewCheckerFunc)

// grouped selector for previews
export const getPreviewState = createStructuredSelector({
    composeTool,
    gettingPreviews,
    subject,
    emailCount,
    htmlContent,
    emailPlaceholderStatus: getEmailPlaceholderStatus,
    previewEmpty: hasEmptyPreview,
    previewIndex: getPreviewIndex,
    previews: getPreviews,
    invalidPlaceholders: getInvalidPlaceholders,
})

/*
 * STATS
 */

export const opens = createBulkEmailSelector("opens", 0)

export const opensWithPrefetched = createBulkEmailSelector("opens_with_prefetched", 0)

export const bounceCount = createBulkEmailSelector("supporter_bounces", 0)

export const replyCount = createBulkEmailSelector("reply_count", 0)

export const unsubscribes = createBulkEmailSelector("unsubscribes", 0)
export const actions = createBulkEmailSelector("actions", 0)

// link breakdown
export const clicks = createBulkEmailSelector("clicks", 0)
export const getImmutableLinkDict = createBulkEmailSelector("link_dict", fromJS({}))
export const linkDict = createToJsSelector(getImmutableLinkDict)

// TODO: rename this variable as it does not necessarily mean that there are replies, just that they route
// selector that returns true if the domain that sent the email routes
export const hasReplies = createSelector(
    fromFullEmailAddress,
    (email) => Userdata.outbox_domain_dict[email.split("@")[1]] !== false,
)

export const isTruncated = createSelector(
    truncationData,
    (truncationDatum) => truncationDatum && truncationDatum.get("is_truncated_sendemail"),
)

// selector that returns true if the domain that sent the email routes
export const doesRoute = createSelector(
    fromFullEmailAddress,
    (email) => Userdata.outbox_domain_dict[email.split("@")[1]] !== false,
)

/*
 * STATUS SELECTORS
 */

export const isCreating = createBulkEmailSelector("posting")
export const isFromCampaign = createBulkEmailSelector("isFromCampaign")
// selector to indicate if you are loading a bulk email
export const isGetting = createBulkEmailSelector("fetching")
export const isLoading = createSelector(isGetting, isCreating, (getting, creating) => getting || creating || false)
export const isPatching = createBulkEmailSelector("patching")
export const isUpdating = createSelector(isLoading, isPatching, (loading, patching) => !!(loading || patching))
export const getIsValidating = createBulkEmailSelector("validating")
export const selectIsValidatingBulkEmail = (bulkEmailId) => createBulkEmailSelector("validating", false, bulkEmailId)
export const isTesting = createBulkEmailSelector("testing")
export const isSending = createBulkEmailSelector("sending", false)
export const selectMustSendAsCreator = createBulkEmailSelector("must_send_as_creator")

/**
 * Check if the current user should be able to edit the "from" line for this email.
 * This should only be allowed if the email's creator and the current user both have
 * the can_send_from_any_outbox permission.
 *
 * We can check the permission for the current user directly, but we must get this
 * information about the email creator from a field on the email.
 *
 * @param {String} mustSendAsCreator true if the email's creator may not send from other outboxes
 * @returns {Boolean} returns true if this email's "from" line can be edited
 */
export const canSendFromAnyOutbox = createSelector(
    selectMustSendAsCreator,
    (mustSendAsCreator) =>
        !mustSendAsCreator && checkPermission(DjangIO.app.models.PermissionType.can_send_from_any_outbox),
)

export const selectShouldDisableSendButton = createSelector(
    selectIsOverPressLimit,
    selectIsABTest,
    emailCount,
    (isOverPressLimit, isABTest, emailCount) => isOverPressLimit || (isABTest && emailCount < AB_TEST_SEND_MINIMUM),
)

export const selectVariantOf = createBulkEmailSelector("variant_of", false)
export const selectWinnerOf = createBulkEmailSelector("winner_of", false)

// If this is a variant or winner of an AB test, root_bulk_email will have the ABTest.bulk_email
// Otherwise, if this is a root AB test BulkEmail or a normal BulkEmail, this is undefined
export const selectRootBulkEmail = createBulkEmailSelector("root_bulk_email", undefined)

/*
 * Compose Page Selectors
 */
// if the page is counting
export const isCounting = createBulkEmailSelector("isCounting")
// disable send buttons when any major action is going on
export const hideSendButtons = createSelector(
    isLoading,
    isTesting,
    isSending,
    getIsValidating,
    (...args) => !!args.find(Boolean),
)
// if any request is in progress, block
export const hasRequestInProgress = createSelector(
    hideSendButtons,
    isPatching,
    (sendRequest, patching) => !!(sendRequest || patching),
)

// selector
export const showBrowse = createBulkEmailSelector("showBrowse")

/*
 * DETAIL PAGE SELECTORS
 */

const getLinkTableObjects = (htmlString, linkObj) => {
    if (!htmlString || !linkObj) {
        return []
    }

    // get all of the links
    const allLinks = getAllMatches(linksAndContentRegex, htmlString || "")
    // use a set to ensure uniqueness
    const linkSet = new Set([])
    const linkResults = []

    allLinks.forEach((match) => {
        // get the match
        const [linkHtmlTag, , rawLink, linkContent] = match
        // clean unused things
        const link = rawLink.replace(/\s|https?:\/\/|\/$/g, "")
        // if the match isn't in the set
        if (link && !linkSet.has(link)) {
            linkSet.add(link) // add to the set
            linkSet.add(rawLink) // add to the set
            linkResults.push({
                link,
                rawLink: rawLink.replace(/\s/g, ""),
                linkHtmlTag: linkHtmlTag.replace(multipleSpacesRegex, ""),
                linkContent: linkContent.replace(multipleSpacesRegex, ""),
                clicks: linkObj[rawLink] || linkObj[link] || 0,
                index: match.index,
            })
        }
    })
    // make sure all links appear (this is just a precaution)
    Object.entries(linkObj).forEach(([link, numClicks]) => {
        if (numClicks && !linkSet.has(link)) {
            linkResults.push({
                link,
                clicks: numClicks,
                rawLink: link,
                linkHtmlTag: link,
                index: 100000, // arbitrary location
            })
        }
    })

    return linkResults
}

/**
 * Selector for getting the links
 *
 * Given the html content and the link dict this returns a list containing
 *  { the cleaned link, the rawlink, the full html including the link,
 *    the content w/in the link, the number of clicks, and the index
 *  }
 */
export const linksSelector = (isMobile = false) =>
    createSelector(
        htmlContent,
        linkDict,
        selectIsABTest,
        selectABTestVariants,
        selectABTestWinner,
        (htmlString, linkObj, isABTest, variants, winner) => {
            if (isMobile) {
                return []
            }

            if (!isABTest) {
                // this is not an AB test; get links only from the BulkEmail itself
                const linkResults = getLinkTableObjects(htmlString, linkObj)
                return linkResults
            }

            // AB tests should get link info from all variants and the winner, but
            // should exclude link info from the base BulkEmail
            const winnerObj = winner && winner.toJS()

            // sort by ID so we can correctly calculate idenfitiers to variants
            const variantsArray = variants && variants.toJS().sort((a, b) => a.id - b.id)

            const linkResults = []

            variantsArray.forEach((variant, idx) => {
                const variantLinks = getLinkTableObjects(variant.html_content, variant.link_dict)
                variantLinks &&
                    variantLinks.forEach((link) => {
                        // add a property showing which vaiant this link came from to show that in the table
                        link.sourceEmail = `Variation ${String.fromCharCode(65 + idx)}` // eslint-disable-line no-param-reassign
                        linkResults.push(link)
                    })
            })

            const winnerLinks = winnerObj && getLinkTableObjects(winnerObj.html_content, winnerObj.link_dict)
            winnerLinks &&
                winnerLinks.forEach((link) => {
                    link.sourceEmail = "Winner" // eslint-disable-line no-param-reassign
                    linkResults.push(link)
                })

            return linkResults
        },
    )
export const hasLinks = (isMobile = false) => createSelector(linksSelector(isMobile), (links) => links.length > 0)

export const bounceRate = createSelector(bounceCount, emailCount, (bounces, total) =>
    total > 0 ? Math.ceil((bounces / total) * 100) : 0,
)

export const selectValidRecipientTypes = () => {
    const { QuorumDataType } = DjangIO.app.models

    const validTypes = []

    if (
        // Staffers are combined w/Officials in Intl regions. See RecipientSelector/helpers.js
        !Userdata.has_international_region &&
        (!Userdata.isOnlyLocalMode() || Userdata.isAllStateAndLocalMode()) &&
        checkPermission(DjangIO.app.models.PermissionType.officials_outbox) &&
        checkPermission(DjangIO.app.models.PermissionType.staffers)
    ) {
        validTypes.push(QuorumDataType.staffer)
    }

    if (checkPermission(DjangIO.app.models.PermissionType.officials_outbox)) {
        validTypes.push(QuorumDataType.person)
    }

    if (checkPermission(DjangIO.app.models.PermissionType.custom_outbox)) {
        validTypes.push(QuorumDataType.supporter)
    }

    if (checkPermission(DjangIO.app.models.PermissionType.media_relations)) {
        validTypes.push(QuorumDataType.press_contact)
    }

    return validTypes
}

/**
 * Get a map of valid recipient types to a boolean of if they should be allowed
 * to be added to the selection.
 *
 * Valid recipient types are all those that the sender has permission to use.
 * Enabled recipient types are, among valid recipient types, which can be
 * still be added based on what has already been used. In AB testing,
 * only a single valid recipient type can be used at a time.
 *
 * @param {Boolean} abTest true if the active email is an AB test; if so, all are enabled
 * @param {Array} validRecipientTypes QDT objects of recipient types that are valid for this email
 * @param {Array} selectionData current recipient selections
 * @returns {Object} map of QDT value (number) -> is enabled (boolean)
 */
export const selectEnabledRecipientTypes = createSelector(
    selectIsABTest,
    selectValidRecipientTypes,
    selection,
    (abTest, validRecipientTypes, selectionData) =>
        validRecipientTypes.reduce((acc, recipientType) => {
            acc[recipientType.value] =
                !abTest ||
                !selectionData.length ||
                selectionData.some((singleSelection) => singleSelection.quorumDataType === recipientType.value)
            return acc
        }, {}),
)

export const selectHasRecipients = createSelector(emailCount, (count) => Boolean(count))

export const selectRecipientCountLabel = createSelector(isCounting, emailCount, (loading, count) => {
    if (loading) {
        return "Counting..."
    }

    return count === 1 ? "1 Recipient" : `${new Intl.NumberFormat().format(count)} Recipients`
})

export const selectExcludedRecipientCountLabel = createSelector(isCounting, excludedEmailCount, (loading, count) => {
    if (loading) {
        return "Counting..."
    }

    return count === 1 ? "1 Recipient" : `${new Intl.NumberFormat().format(count)} Recipients`
})

/*
 * GROUPED SELECTORS
 */

// These are the state things for the header
export const headerSelectors = createStructuredSelector({
    id: getId,
    subject,
    isScheduled,
    scheduledSendMoment,
    fromName: formattedFromName,
    replyToName: formattedReplyToName,
})

// for toggles
export const personEmailToggleSelectors = createStructuredSelector({
    usePersonalEmail,
})

export const ccToggleSelectors = createStructuredSelector({
    shouldCcAll,
})

export const sendFromToggleSelectors = createStructuredSelector({
    isActive: sendFromAssignments,
    booleanField: () => "send_from_assignments",
    actionString: () => "Send From",
    helpText: () => outboxFinalizeConstants.SEND_FROM_ASSIGNED_TEAM_MEMBER_TEXT,
})

export const replyToToggleSelectors = createStructuredSelector({
    isActive: replyToAssignments,
    booleanField: () => "reply_to_assignments",
    actionString: () => "Reply To",
    helpText: () => outboxFinalizeConstants.REPLY_TO_ASSIGNED_TEAM_MEMBER_TEXT,
})

/**
 * More AB Testing Selectors
 */
export const selectABTestId = createABTestSelector("id")
export const selectABTestBestVariantId = createABTestSelector("best_variant_id")
export const selectABTestName = createABTestSelector("name")
export const selectABTestDescription = createABTestSelector("description")
export const selectABTestVariable = createABTestSelector("test_variable")
export const selectABTestMode = createABTestSelector("test_mode")
export const selectABTestWinCondition = createABTestSelector("win_condition")
export const selectABTestStartTime = createSelector(
    createABTestSelector("start_time"),
    (startTime) => startTime && moment(startTime),
)
export const selectABTestEndTime = createSelector(
    selectABTestStartTime,
    createABTestSelector("end_time"),
    (startTime, endTime) => {
        if (startTime && !endTime) {
            return moment(startTime).add(6, "hours")
        }
        return endTime && moment(endTime)
    },
)
export const selectABTestStatus = createABTestSelector("current_status")

export const selectIsScheduledOrRunning = createSelector(
    isScheduled,
    selectABTestStatus,
    (_isScheduled, abTestStatus) =>
        _isScheduled ||
        (abTestStatus && abTestStatus !== DjangIO.app.userdata.outbox.types.ABTestStatus.complete.value),
)

export const selectABTestDistribution = createSelector(
    selectABTestMode,
    createABTestSelector("distribution"),
    (testMode, distribution) => {
        switch (testMode) {
            case DjangIO.app.userdata.outbox.types.ABTestMode.send_best.value:
                if (distribution && distribution.size === 3) {
                    return distribution
                }

                return fromJS([
                    { label: "Variation A", value: 0.25 },
                    { label: "Variation B", value: 0.25 },
                    { label: "Winner", value: 0.5 },
                ])

            case DjangIO.app.userdata.outbox.types.ABTestMode.learning.value:
                if (distribution && distribution.size === 2) {
                    return distribution
                }

                return fromJS([
                    { label: "Variation A", value: 0.5 },
                    { label: "Variation B", value: 0.5 },
                ])

            default:
                return fromJS([
                    { label: "Variation A", value: 0.5 },
                    { label: "Variation B", value: 0.5 },
                ])
        }
    },
)

export const selectAdjustedABTestDistribution = createSelector(
    selectABTestDistribution,
    emailCount,
    (distribution, count) =>
        distribution.map((item) =>
            item.merge({
                // values are stored as %; slider wants the real count
                value: count
                    ? item.get("value") * count
                    : // if we don't have any counts, just use the actual percentages
                      item.get("value"),
            }),
        ),
)

export const selectABTestPatchableFields = createSelector(
    selectIsABTest,
    createStructuredSelector({
        name: selectABTestName,
        description: selectABTestDescription,
        distribution: selectABTestDistribution,
        test_mode: selectABTestMode,
        test_variable: selectABTestVariable,
        win_condition: selectABTestWinCondition,
        start_time: selectABTestStartTime,
        end_time: selectABTestEndTime,
    }),
    (isABTest, abTestPatchableFields) => (isABTest ? abTestPatchableFields : null),
)

export const selectIsABTestingBody = createSelector(
    selectABTestVariable,
    (testVariable) => testVariable === DjangIO.app.userdata.outbox.types.ABTestVariable.body.value,
)

// AB variant - body html content
export const makeABTestBodyHTMLContent = (variantId) =>
    createSelector(createBulkEmailSelector("html_content", "", variantId), (uncleanHtmlContent) =>
        uncleanHtmlContent.replace(htmlQuoteRegex, "'"),
    )

// AB variant - body attachments
const getImmutableAttachmentsABTest = (variantId) => createBulkEmailSelector("attachments", [], variantId)

export const getAttachmentsABTest = (variantId) =>
    createSelector(getImmutableAttachmentsABTest(variantId), (attachmentList) => {
        const attachmentObjs = []
        if (attachmentList.length || attachmentList.size > 0) {
            attachmentList.forEach((a) => {
                attachmentObjs.push({
                    id: a.get("id"),
                    name: a.get("name"),
                    size: a.get("size"),
                    mimeType: a.get("mime_type"),
                    base64Data: a.get("content"),
                    canPreview: PREVIEWABLE_FIELDS.some(
                        (currRegex) => a.get("mime_type") && currRegex.test(a.get("mime_type").toLowerCase()),
                    ),
                    duplicated_from: a.get("duplicated_from"),
                })
            })
        }
        return attachmentObjs
    })

export const selectVariantAttachmentUris = (variantId) =>
    createSelector(getAttachmentsABTest(variantId), (attachmentList) =>
        attachmentList.map((attachment) => DjangIO.app.userdata.models.Attachment.foreignKey(attachment.id)),
    )

// AB variant - bee plugin
const getBeeImmutableABTest = (variantId) => createBulkEmailSelector("beePluginInstance", {}, variantId)
export const getBeePluginABTest = (variantId) => createToJsSelector(getBeeImmutableABTest(variantId))

// AB variant - the bee json
const getImmutableJsonContentABTest = (variantId) => createBulkEmailSelector("json_content", {}, variantId)
export const getJsonContentABTest = (variantId) => createToJsSelector(getImmutableJsonContentABTest(variantId))

// AB variant body - preview controls
const gettingPreviewsABTest = (variantId) => createBulkEmailSelector("gettingPreviews", false, variantId)
export const getPreviewIndexABTest = (variantId) => createBulkEmailSelector("previewIndex", 0, variantId)
export const getEmailPlaceholderStatusABTest = (variantId) =>
    createBulkEmailSelector("emailPlaceholderStatus", EmailPreviewStatusCodes.READY, variantId)

// AB variant body - gets the preview objects
const getImmutablePreviewsABTest = (variantId) => createBulkEmailSelector("previews", fromJS([]), variantId)
export const getPreviewsABTest = (variantId) => createToJsSelector(getImmutablePreviewsABTest(variantId))

export const hasEmptyPreviewABTest = (variantId) =>
    createVariantSelector({
        variantId,
        inputSelectors: [getPreviewsABTest],
        resultFunc: emptyPreviewCheckerFunc,
    })

// AB variant body - gets the array of invalid placeholders
const getImmutableInvalidPlaceholdersABTestVariant = (variantId) =>
    createBulkEmailSelector("invalidPlaceholders", [], variantId)
export const getInvalidPlaceholdersABTestVariant = (variantId) =>
    createToJsSelector(getImmutableInvalidPlaceholdersABTestVariant(variantId))

// AB variant
export const selectIsABTestingSubject = createSelector(
    selectABTestVariable,
    (testVariable) => testVariable === DjangIO.app.userdata.outbox.types.ABTestVariable.subject.value,
)

// These are all of the fields that could differ between variants.
// We enumerate them here to quickly reset them when swapping between test variables.
// We also empty the attachments here, so it really should only be used for resetting fields when changing test types.
export const selectBulkEmailFieldsForABTests = createSelector(
    subject,
    composeTool,
    htmlContent,
    getJsonContent,
    fromUser,
    fromName,
    createBulkEmailSelector("from_address"),
    (subject, compose_tool, html_content, json_content, from_user, from_name, from_address) => ({
        subject,
        compose_tool,
        html_content,
        json_content,
        from_user,
        from_name,
        from_address,
        attachments: [],
    }),
)

export const testTypeConnection = createStructuredSelector({
    bulkEmailFieldsForABTests: selectBulkEmailFieldsForABTests,
    name: selectABTestName,
    description: selectABTestDescription,
    testVariable: selectABTestVariable,
})

/*
 * For generating bulk email - this will be useful later
 */
// TODO: I truly dislike this. Why do we need it here and nowhere else?
export const selectPatchableFields = createStructuredSelector({
    attachments: attachmentUris,
    bcc_rcpts: bccRcpts,
    cc_rcpts: ccRcpts,
    compose_tool: composeTool,
    content_version: contentVersion, // Not patchable, but used to verify we've got the latest version
    current_status: currentStatus,
    excludes_json: excludesJson,
    external_id: externalId,
    from_address: makeFromEmail,
    from_name: fromName,
    from_user: fromUser,
    html_content: htmlContent,
    is_custom_unsubscribe: isCustomUnsubscribe,
    is_draft: isDraft, // overwritten in regenerate
    is_scheduled: isScheduled,
    limited_to_teams: selectLimitedToTeamsForBulkEmailForm,
    json_content: getJsonContent,
    projects: projectUris,
    reply_to_address: replyToFullEmailAddress,
    reply_to_name: replyToName,
    reply_to_user: replyToUser,
    scheduled_send_time: scheduledSendTime,
    selection_json: selectionJson,
    should_cc_all: shouldCcAll,
    subject,
    unsubscribe_enabled: unsubscribeEnabled,
    use_personal_email: usePersonalEmail,
    send_from_assignments: sendFromAssignments,
    reply_to_assignments: replyToAssignments,
    abtest: selectABTestPatchableFields,
})

/*
 * For recipient tool
 */
export const recipientToolSelector = createStructuredSelector({
    bulkEmailId: getBulkEmailId,
    dbId: getId,
    emailCount,
    enabledRecipientTypes: selectEnabledRecipientTypes,
    excludedEmailCount,
    excludedRecipientCountLabel: selectExcludedRecipientCountLabel,
    excludes,
    isABTest: selectIsABTest,
    isCounting,
    isOverPressLimit: selectIsOverPressLimit,
    personCount,
    pressCount: selectPressCount,
    recipientCountLabel: selectRecipientCountLabel,
    selection,
    shouldRenderPersonalToggles,
    showBrowse,
    supporterCount,
    usePersonalEmail,
    validRecipientTypes: selectValidRecipientTypes,
})

export const templateSelector = createStructuredSelector({
    compose_tool: composeTool,
    html_content: htmlContent,
    json_content: getJsonContent,
    attachments: attachmentUris,
})

export const selectABTestStartTimeValidationError = createSelector(
    selectABTestStartTime,
    selectABTestEndTime,
    (startTime, endTime) => {
        if (!startTime && endTime) {
            // can't have end time without start time
            return outboxFinalizeConstants.END_TIME_WITHOUT_START_TIME
        }

        // Check if startTime is before 10 minutes from now
        if (startTime && startTime.isBefore(moment().add(10, "minutes"))) {
            return outboxFinalizeConstants.START_TOO_SOON_TEXT
        }
        return undefined
    },
)

export const selectABTestEndTimeValidationError = createSelector(
    selectABTestStartTime,
    selectABTestEndTime,
    (startTime, endTime) => {
        if (!startTime && !endTime) {
            // Allow endTime to be blank if startTime is also blank
            return undefined
        }

        if (!startTime && endTime) {
            // can't have end time with no start time, but that is
            // an error on the start time field, not this field
            return undefined
        }

        // Check if endTime is before startTime
        if (endTime.isBefore(startTime)) {
            return outboxFinalizeConstants.END_BEFORE_START_ERROR_TEXT
        }

        // Check if endTime is less than 2 hours after startTime
        if (startTime.clone().add(2, "hours").isAfter(endTime)) {
            return outboxFinalizeConstants.END_TOO_SOON_ERROR_TEXT
        }

        // Check if endTime is more than 7 days after startTime
        if (startTime.clone().add(7, "days").isBefore(endTime)) {
            return outboxFinalizeConstants.END_TOO_LATE_ERROR_TEXT
        }

        return undefined
    },
)

// helper for isMessageValid selectors
const validateMessage = (patchedFields, numRecipients) => {
    /* Validate the Headers (to, from, bcc, cc) */
    if (!patchedFields.from_user && !(patchedFields.from_name && patchedFields.from_address)) {
        // don't have a user for it to from come AND
        // neither a name nor email address
        return validationContants.FROM_REQUIRED_FIELDS
    }

    if (!emailValidationRegex.test(patchedFields.from_address)) {
        // make sure the email address is valid
        return validationContants.FROM_INVALID_EMAIL
    }

    if (!patchedFields.reply_to_user && !(patchedFields.reply_to_name && patchedFields.reply_to_address)) {
        // have custom reply to but no reply name or email
        return validationContants.REPLY_REQUIRED_FIELDS
    }

    if (!emailValidationRegex.test(patchedFields.reply_to_address)) {
        // valid reply email
        return validationContants.REPLY_INVALID_EMAIL
    }

    if (patchedFields.bcc_rcpts.length && !patchedFields.bcc_rcpts.every((email) => emailValidationRegex.test(email))) {
        // the bcc emails are correct
        return validationContants.BCC_INVALID_EMAIL
    }

    if (patchedFields.cc_rcpts.length && !patchedFields.cc_rcpts.every((email) => emailValidationRegex.test(email))) {
        // the cc emails are correct
        return validationContants.CC_INVALID_EMAIL
    }

    /* validate content */
    if (numRecipients < 1) {
        // haven't chosen any recipients
        return validationContants.MUST_HAVE_RECIPIENTS
    }

    if (!patchedFields.subject) {
        // no subject
        return validationContants.MUST_HAVE_SUBJECT
    }

    if (patchedFields.html_content.length < 10) {
        // no content in the email
        return validationContants.MUST_HAVE_CONTENT
    }

    if (spaceInsidePlaceholder.test(patchedFields.html_content.replace(grabInsideStyle))) {
        // if they have spaces in their placeholders
        return validationContants.SPACE_IN_PLACEHOLDER
    }

    if (slashInsidePlaceholder.test(patchedFields.html_content.replace(grabInsideStyle))) {
        // if they have slashes in their placeholders
        return validationContants.SLASH_IN_PLACEHOLDER
    }

    if (!validateHtmlContent(patchedFields.html_content)) {
        return validationContants.CONTAINS_IFRAME
    }

    if (containsImagesOnly(patchedFields)) {
        return validationContants.CONTAINS_IMAGE_ONLY
    }

    if (containsPlaceholdersOnly(patchedFields)) {
        return validationContants.CONTAINS_PLACEHOLDERS_ONLY
    }

    return ""
}

// for email validation
export const isMessageValid = createSelector(selectPatchableFields, emailCount, validateMessage)

// profiles-new
export const selectProfileTitle = createSelector(
    selectIsABTest,
    subject,
    selectABTestDescription,
    (isABTest, subject, description) => (isABTest ? description : subject),
)
export const selectBulkEmailCustomDescription = selectSpecificCustomDescription(
    DjangIO.app.models.QuorumDataType.bulk_email.key,
)
const makeBulkEmailDescriptionSelect = makeSelectFieldFromCustomDescription(selectBulkEmailCustomDescription)
export const selectCurrentTab = makeBulkEmailDescriptionSelect("currentTab")

export const selectSubtitleFirstLine = createSelector(
    formattedFromName,
    formattedReplyToName,
    sendFromAssignments,
    replyToAssignments,
    (fromName, replyTo, sendFromAssignments, replyToAssignments) => {
        return (
            `From: ${fromName}` +
            `${sendFromAssignments ? " (Sent From Assignments)" : ""}` +
            `${replyTo ? `  |  Reply To: ${replyTo}` : ""}` +
            `${replyToAssignments ? " (Reply To Assignments)" : ""}`
        )
    },
)

export const makeFormattedTimeSelector = (timeSelector) =>
    createSelector(timeSelector, (time) => {
        if (!time) {
            return undefined
        }
        /*
        moving this import to up top
        // need to import moment and moment-timezone
        const momentTimezone = require("moment-timezone")
        */
        const TIME_ZONE = momentTimezone.tz(momentTimezone.tz.guess()).format("z")
        const TIME_FORMAT = `MMMM Do, YYYY h:mm a [${TIME_ZONE}]`
        return time.format(TIME_FORMAT)
    })

export const selectSubtitleSecondLine = createSelector(
    isScheduled,
    makeFormattedTimeSelector(scheduledSendMoment),
    (isScheduled, sentTime) => {
        return `${isScheduled ? "Scheduled Send Time: " : "Sent At: "}${sentTime}`
    },
)

// select the data object for the sent status / time in the text section on the overview tab
export const scheduleStatusObject = createSelector(
    isScheduled,
    makeFormattedTimeSelector(scheduledSendMoment),
    (isScheduled, sentTime) => ({
        title: `${isScheduled ? "Scheduled Send Time" : "Sent"}`,
        subtitle: sentTime,
    }),
)

const { ABTestStatus } = DjangIO.app.userdata.outbox.types

export const selectIsABTestScheduled = createSelector(
    selectABTestStatus,
    (testStatus) =>
        testStatus in
        [
            ABTestStatus.draft.value,
            ABTestStatus.unprocessed.value,
            ABTestStatus.processing.value,
            ABTestStatus.scheduled.value,
        ],
)

export const selectIsABTestCompleted = createSelector(
    selectABTestStatus,
    (testStatus) => testStatus === ABTestStatus.complete.value,
)

export const selectABTestStartObject = createSelector(
    selectIsABTestScheduled,
    makeFormattedTimeSelector(selectABTestStartTime),
    (isTestScheduled, abTestStartTime) => ({
        title: `${isTestScheduled ? "Scheduled Start Time" : "Started"}`,
        subtitle: abTestStartTime,
    }),
)

export const selectABTestEndObject = createSelector(
    selectIsABTestCompleted,
    makeFormattedTimeSelector(selectABTestEndTime),
    (isTestCompleted, abTestEndTime) => ({
        title: `${isTestCompleted ? "Ended" : "Scheduled End Time"}`,
        subtitle: abTestEndTime,
    }),
)

export const verboseStatus = createSelector(isScheduled, currentStatus, (isScheduled, currentStatus) => {
    const statusObj = DjangIO.app.userdata.outbox.types.EmailStatus.by_value(currentStatus)

    if (!statusObj) {
        return undefined
    }
    return DjangIO.app.userdata.outbox.types.EmailStatus.by_value(currentStatus)[
        isScheduled ? "scheduled_name" : "verbose_name"
    ]
})

export const verboseTestStatus = createSelector(selectABTestStatus, (testStatus) => {
    const statusObj = DjangIO.app.userdata.outbox.types.ABTestStatus.by_value(testStatus)

    if (!statusObj) {
        return undefined
    }
    return statusObj.label
})

export const selectTitleObject = createSelector(selectIsABTest, selectProfileTitle, (isABTest, subtitle) =>
    isABTest ? { title: "Description", subtitle } : { subtitle },
)

// select the data object for the email status in the text section on the overview tab
export const selectStatusObject = createSelector(
    selectIsABTest,
    verboseTestStatus,
    verboseStatus,
    (isABTest, verboseTestStatus, verboseStatus) => ({
        title: isABTest ? "Test Status" : "Status",
        subtitle: isABTest ? verboseTestStatus : verboseStatus,
    }),
)

export const selectEarliestABTestEmailSendTime = createSelector(
    selectABTestVariants,
    scheduledSendMoment,
    (variants, baseScheduledSendTime) => {
        let earliestSendTime = moment(baseScheduledSendTime)
        variants.forEach((variant, index) => {
            const variantSendTime = moment(variant.get("scheduled_send_time"))
            if (index === 0 && variantSendTime.isBefore(earliestSendTime)) {
                earliestSendTime = variantSendTime
            } else if (index > 0 && variantSendTime.isBefore(earliestSendTime)) {
                earliestSendTime = variantSendTime
            }
        })
        return earliestSendTime
    },
)

export const shouldRenderCancelButton = createSelector(
    isScheduled,
    scheduledSendMoment,
    selectIsABTest,
    selectEarliestABTestEmailSendTime,
    (isScheduled, sentTime, isABTest, earliestABTestEmailSendTime) => {
        if (isABTest) {
            return isScheduled && earliestABTestEmailSendTime.isAfter(moment().add(10, "minutes"))
        }
        return isScheduled && sentTime.isAfter(moment().add(10, "minutes"))
    },
)

// select the data objects for the counter row on the overview tab
export const counterRowData = createSelector(
    opens,
    opensWithPrefetched,
    clicks,
    unsubscribes,
    emailCount,
    bounceCount,
    bounceRate,
    (opens, opensWithPrefetched, clicks, unsubscribes, emailCount, bounceCount, bounceRate) => [
        { title: "Unique Opens", value: opens, tooltipText: outboxConstants.TOOLTIP_TO_OPENS },
        ...(isFeatureEnabled("ff_outbox_open_with_prefetched_stat")
            ? [
                  {
                      title: "Unique Opens Including Prefetched Opens",
                      value: opensWithPrefetched,
                      tooltipText: outboxConstants.TOOLTIP_TO_OPENS_WITH_PREFETCHED,
                  },
              ]
            : []),
        { title: "Unique Clicks", value: clicks },
        { title: "Unsubscribes", value: unsubscribes },
        { title: "Total Emailed", value: emailCount },
        { title: "Unique Contact Bounces", value: bounceCount },
        { title: "Unique Contact Bounce Percentage", value: bounceRate },
    ],
)

export const selectABTestOpens = createSelector(selectABTestVariants, selectABTestWinner, (variants, winner) => {
    const variantOpens = variants.reduce((totalOpens, variant) => totalOpens + variant.get("opens", 0), 0)
    const winnerOpens = winner ? winner.get("opens", 0) : 0
    return variantOpens + winnerOpens
})

export const selectABTestOpensWithPrefetched = createSelector(
    selectABTestVariants,
    selectABTestWinner,
    (variants, winner) => {
        const variantOpens = variants.reduce(
            (totalOpens, variant) => totalOpens + variant.get("opens_with_prefetched", 0),
            0,
        )
        const winnerOpens = winner ? winner.get("opens_with_prefetched", 0) : 0
        return variantOpens + winnerOpens
    },
)

export const selectABTestClicks = createSelector(selectABTestVariants, selectABTestWinner, (variants, winner) => {
    const variantClicks = variants.reduce((totalClicks, variant) => totalClicks + variant.get("clicks", 0), 0)
    const winnerClicks = winner ? winner.get("clicks", 0) : 0
    return variantClicks + winnerClicks
})

export const selectABTestUnsubscribes = createSelector(selectABTestVariants, selectABTestWinner, (variants, winner) => {
    const variantUnsubscribes = variants.reduce(
        (totalUnsubs, variant) => totalUnsubs + variant.get("unsubscribes", 0),
        0,
    )
    const winnerUnsubscribes = winner ? winner.get("unsubscribes", 0) : 0
    return variantUnsubscribes + winnerUnsubscribes
})

export const selectABTestBounceCount = createSelector(selectABTestVariants, selectABTestWinner, (variants, winner) => {
    const variantBounces = variants.reduce(
        (totalBounces, variant) => totalBounces + variant.get("supporter_bounces", 0),
        0,
    )
    const winnerBounces = winner ? winner.get("supporter_bounces", 0) : 0
    return variantBounces + winnerBounces
})

/**
 * Note that this is the email_count already sent out as Variants.
 * It will not include the Winner's email_count until the end of the test.
 * We don't want to include those in the bounce rate calculation until they are sent!
 */
export const selectABTestSentEmailCount = createSelector(
    selectABTestVariants,
    selectABTestWinner,
    (variants, winner) => {
        const variantsEmailCount = variants.reduce(
            (totalCount, variant) => totalCount + variant.get("email_count", 0),
            0,
        )

        if (winner && winner.current_status in [EmailStatus.sent.value, EmailStatus.delivered.value]) {
            return winner.get("email_count", 0) + variantsEmailCount
        }

        return variantsEmailCount
    },
)

// We want to use the already sent out email_count to calculate the bounce_rate,
// so it is more representative of the current state.
export const selectABTestBounceRate = createSelector(
    selectABTestBounceCount,
    selectABTestSentEmailCount,
    (bounces, total) => (total > 0 ? Math.ceil((bounces / total) * 100) : 0),
)

export const selectABTestCounterRowData = createSelector(
    selectABTestOpens,
    selectABTestOpensWithPrefetched,
    selectABTestClicks,
    selectABTestUnsubscribes,
    emailCount,
    selectABTestBounceCount,
    selectABTestBounceRate,
    (totalOpens, totalOpensWithPrefetched, totalClicks, totalUnsubs, count, totalBounces, totalBounceRate) => [
        { title: "Unique Opens", value: totalOpens, tooltipText: outboxConstants.TOOLTIP_TO_OPENS },
        ...(isFeatureEnabled("ff_outbox_open_with_prefetched_stat")
            ? [
                  {
                      title: "Unique Opens Including Prefetched Opens",
                      value: totalOpensWithPrefetched,
                      tooltipText: outboxConstants.TOOLTIP_TO_OPENS_WITH_PREFETCHED,
                  },
              ]
            : []),
        { title: "Unique Clicks", value: totalClicks },
        { title: "Unsubscribes", value: totalUnsubs },
        { title: "Total Emailed", value: count, tooltipText: outboxConstants.TOOLTIP_TO_EMAIL_COUNT },
        { title: "Unique Contact Bounces", value: totalBounces },
        { title: "Unique Contact Bounce Percentage", value: totalBounceRate },
    ],
)

export const selectABTestChildrenIds = createExtraFieldSelector({
    objectSelector: createBulkEmailSelector,
    key: "abtest_children",
})

// select the data objects for the "Details" data table view on the overview tab
export const detailsTableData = createSelector(
    selectIsABTest,
    formattedFromName,
    formattedReplyToName,
    creator,
    supporterCount,
    verboseStatus,
    verboseTestStatus,
    ccRcpts,
    bccRcpts,
    (isABTest, fromName, replyName, creator, supporterCount, verboseStatus, verboseTestStatus, ccRcpts, bccRcpts) => {
        // convert an array of CC rcpts to string of comma separated emails
        const ccReducer = (accStr, rcpt) => {
            if (accStr !== "") {
                return `${accStr}, ${rcpt}`
            }
            return rcpt
        }

        return [
            ...(!isABTest
                ? [
                      {
                          title: "From",
                          subtitle: fromName,
                      },
                  ]
                : []),
            {
                title: "Reply To",
                subtitle: replyName,
            },
            {
                title: "Creator",
                subtitle: creator,
            },
            {
                title: "Custom Contacts",
                subtitle: `${supporterCount} Emailed`,
            },
            ...(!isABTest
                ? [
                      {
                          title: "Status",
                          subtitle: verboseStatus,
                      },
                  ]
                : [
                      {
                          title: "Test Status",
                          subtitle: verboseTestStatus,
                      },
                  ]),
            {
                title: "CC",
                subtitle: ccRcpts.length ? ccRcpts.reduce(ccReducer, "") : "N/A",
            },
            {
                title: "BCC",
                subtitle: bccRcpts.length ? bccRcpts.reduce(ccReducer, "") : "N/A",
            },
        ]
    },
)

// select the data objects for the "A/B Test Details" data table view on the overview tab
export const selectABTestTableData = createSelector(
    selectABTestVariable,
    emailCount,
    selectABTestWinCondition,
    makeFormattedTimeSelector(selectABTestStartTime),
    (testVariable, recipientCount, winningCriteria, testStart) => [
        {
            title: "Test Conducted",
            subtitle: testVariable && DjangIO.app.userdata.outbox.types.ABTestVariable.by_value(testVariable).label,
        },
        {
            title: "Number of Recipients",
            subtitle: new Intl.NumberFormat().format(recipientCount),
        },
        {
            title: "Winning Criteria",
            subtitle: winningCriteria && DjangIO.app.userdata.outbox.types.ABTestMetric.by_value(winningCriteria).label,
        },
        {
            title: "Scheduled Send",
            subtitle: testStart,
        },
    ],
)

/**
 * @name createVariantSelector
 *
 * Helper function that accepts a variantId and calls all inputSelectors with that argument
 *
 * @param {object} params
 * @param {number} [params.variantId] The BulkEmail ID of the variant being selected
 * @param {function[]} [params.inputSelectors] Array of selectors feeding into resultFunc
 * @param {function} [params.resultFunc] Function accepting args from inputSelectors and returning the result
 * @returns Memoized selector function
 */
export const createVariantSelector = ({ variantId, inputSelectors, resultFunc }) => {
    const selectedArgs = inputSelectors.map((selector) => selector(variantId))
    return createSelector(...selectedArgs, resultFunc)
}

export const selectVariantComposeTool = (variantId) => createBulkEmailSelector("compose_tool", "summernote", variantId)
export const selectVariantHtmlContent = (variantId) => createBulkEmailSelector("html_content", "", variantId)
const variantHtmlContent = (variantId) =>
    createSelector(
        selectVariantHtmlContent(variantId),
        // we need to get rid of all the '&quot;' characters in the html content
        // because of the way summernote parses fonts. If we don't replace these,
        // multi-word font names like "Times New Roman" will be parsed incorrectly
        // and default to Helvetica Neue
        (uncleanHtmlContent) => uncleanHtmlContent.replace(htmlQuoteRegex, "'"),
    )
const selectVariantEmailCount = (variantId) => createBulkEmailSelector("email_count", null, variantId)
const selectVariantOpens = (variantId) => createBulkEmailSelector("opens", null, variantId)
const selectVariantOpensWithPrefetched = (variantId) =>
    createBulkEmailSelector("opens_with_prefetched", null, variantId)
export const selectVariantSubject = (variantId) => createBulkEmailSelector("subject", null, variantId)
const selectVariantOpenRate = (variantId) => createBulkEmailSelector("open_rate", null, variantId)
const selectVariantClickRate = (variantId) => createBulkEmailSelector("click_rate", null, variantId)

const selectVariantIdentifier = (variantId) =>
    createSelector(selectABTestVariantIds, selectABTestWinnerId, (variantIds, winnerId) => {
        if (variantId === winnerId) {
            return "Winner"
        }

        // Variant A is always the first one created, and proceed from there
        const index = variantIds.sort().findIndex((id) => id === variantId)
        return `Variation ${String.fromCharCode(65 + index)}`
    })

// TODO: These are only used in the Details page right now, so we aren't including any domain validating logic
// This is okay, because these emails have all been sent, so they must have already gone through that validation
const selectVariantFromName = (variantId) => createBulkEmailSelector("from_name", null, variantId)
const selectVariantFromUser = (variantId) => createBulkEmailSelector("from_user", null, variantId)
const selectVariantFromAddress = (variantId) => createBulkEmailSelector("from_address", null, variantId)
const selectVariantFromAssignments = (variantId) => createBulkEmailSelector("send_from_assignments", null, variantId)
const selectVariantSender = (variantId) =>
    createVariantSelector({
        variantId,
        inputSelectors: [selectVariantFromAddress, selectVariantFromName],
        resultFunc: (address, name) => `${name} | ${address}`,
    })

export const selectVariantFromAddressUsername = (variantId) =>
    createVariantSelector({
        variantId,
        inputSelectors: [selectVariantFromAddress],
        resultFunc: fromAddressResultFunc,
    })

export const selectVariantFromDomain = (variantId) =>
    createVariantSelector({
        variantId,
        inputSelectors: [selectVariantFromAddress],
        resultFunc: fromDomainResultFunc,
    })

export const selectVariantFromUserId = (variantId) =>
    createVariantSelector({
        variantId,
        inputSelectors: [selectVariantFromUser],
        resultFunc: fromIdResultFunc,
    })

export const selectVariantFromEmail = (variantId) =>
    createVariantSelector({
        variantId,
        inputSelectors: [selectVariantFromAddressUsername, selectVariantFromDomain],
        resultFunc: makeFromEmailResultFunc,
    })

export const selectVariantPatchableFields = (variantId) =>
    createStructuredSelector({
        subject: getABTestedSubject(variantId),
        attachments: selectVariantAttachmentUris(variantId),
        bcc_rcpts: bccRcpts,
        cc_rcpts: ccRcpts,
        compose_tool: selectVariantComposeTool(variantId),
        from_address: selectVariantFromEmail(variantId),
        from_name: selectVariantFromName(variantId),
        from_user: selectVariantFromUser(variantId),
        html_content: makeABTestBodyHTMLContent(variantId),
        is_custom_unsubscribe: isCustomUnsubscribe,
        json_content: getJsonContentABTest(variantId),
        reply_to_address: replyToFullEmailAddress,
        reply_to_name: replyToName,
        reply_to_user: replyToUser,
        should_cc_all: shouldCcAll,
        unsubscribe_enabled: unsubscribeEnabled,
        use_personal_email: usePersonalEmail,
        send_from_assignments: selectVariantFromAssignments(variantId),
        reply_to_assignments: replyToAssignments,
    })

/**
 * This is a mess, but it's the same logic as isMessageValid, so we'll leave it for now.
 * The only difference is accepting a bulkEmailId and dropping the awful switch pattern.
 */
export const selectIsMessageValid = (bulkEmailId) =>
    createSelector(selectVariantPatchableFields(bulkEmailId), emailCount, validateMessage)

export const selectABTestVariantTableData = (variantId) =>
    createVariantSelector({
        variantId,
        inputSelectors: [
            selectVariantEmailCount,
            selectVariantOpens,
            selectVariantOpensWithPrefetched,
            selectVariantSubject,
            selectVariantOpenRate,
            selectVariantClickRate,
            selectVariantSender,
        ],
        resultFunc: (
            variantEmailCount,
            variantOpens,
            variantOpensWithPrefetched,
            variantSubject,
            variantOpenRate,
            variantClickRate,
            variantSender,
        ) => {
            const { format: numberFormat } = new Intl.NumberFormat()
            const { format: percentFormat } = new Intl.NumberFormat(undefined, { style: "percent" })

            const openRate = Number(variantOpenRate)
            const clickRate = Number(variantClickRate)

            const formattedOpenClickRate = openRate ? percentFormat(clickRate / openRate) : "N/A"

            return [
                { title: "Emails Sent", subtitle: numberFormat(variantEmailCount) },
                { title: "Emails Opened", subtitle: numberFormat(variantOpens) },
                ...(isFeatureEnabled("ff_outbox_open_with_prefetched_stat")
                    ? [
                          {
                              title: "Emails Opened Including Prefetched Opens",
                              subtitle: numberFormat(variantOpensWithPrefetched),
                          },
                      ]
                    : []),
                { title: "Subject Line", subtitle: variantSubject },
                {}, // Empty cell to force newline
                { title: "Open Rate", subtitle: percentFormat(variantOpenRate) },
                { title: "Click-Through Rate", subtitle: percentFormat(variantClickRate) },
                { title: "Click-To-Open Rate", subtitle: formattedOpenClickRate },
                { title: "Sender Name & Email", subtitle: variantSender },
            ]
        },
    })

export const selectVariantFieldsForTemplate = (variantId) =>
    createStructuredSelector({
        compose_tool: selectVariantComposeTool(variantId),
        html_content: variantHtmlContent(variantId),
        json_content: getJsonContentABTest(variantId),
        attachments: selectVariantAttachmentUris(variantId),
    })

export const selectIsPreviewDisabled = createSelector(
    composeTool,
    getJsonContent,
    htmlContent,
    (_composeTool, jsonContent, _htmlContent) => {
        switch (_composeTool) {
            case "summernote":
                return _htmlContent === ""
            case "beefree":
                return isEqual(jsonContent, blankBeeFree)
            default:
                return true
        }
    },
)

// AB variant - grouped selector for Email Content variable previews
export const getPreviewStateABTestVariant = createStructuredSelector({
    composeTool: (state, props) => selectVariantComposeTool(props.variantId)(state),
    emailCount,
    emailPlaceholderStatus: (state, props) => getEmailPlaceholderStatusABTest(props.variantId)(state),
    gettingPreviews: (state, props) => gettingPreviewsABTest(props.variantId)(state),
    htmlContent: (state, props) => selectVariantHtmlContent(props.variantId)(state),
    invalidPlaceholders: (state, props) => getInvalidPlaceholdersABTestVariant(props.variantId)(state),
    previewEmpty: (state, props) => hasEmptyPreviewABTest(props.variantId)(state),
    previewIndex: (state, props) => getPreviewIndexABTest(props.variantId)(state),
    previews: (state, props) => getPreviewsABTest(props.variantId)(state),
    subject,
})

export const abTestedSubjectConnection = createStructuredSelector({
    isPreviewDisabled: selectIsPreviewDisabled,
    variants: selectABTestVariants,
    placeholders: getPlaceholders,
})

export const abTestedSenderConnection = createStructuredSelector({
    isPreviewDisabled: selectIsPreviewDisabled,
    variants: selectABTestVariants,
})

// These are the state things for the body
export const overviewSelectors = createStructuredSelector({
    abTestDistribution: selectAdjustedABTestDistribution,
    abTestEndObject: selectABTestEndObject,
    abTestStartObject: selectABTestStartObject,
    abTestTableData: selectABTestTableData,
    abTestVariantIds: selectABTestVariantIds,
    abTestWinnerId: selectABTestWinnerId,
    actions,
    bccRcpts,
    ccRcpts,
    composeTool,
    counterRowData,
    counterRowDataABTest: selectABTestCounterRowData,
    detailOptions: selectDetailOptions,
    detailsTableData,
    doesRoute,
    emailCount,
    htmlContent,
    id: getId,
    isABTest: selectIsABTest,
    isLoading,
    isScheduledOrRunning: selectIsScheduledOrRunning,
    replyCount,
    scheduleStatusObject,
    selectionJson,
    statusObject: selectStatusObject,
    subject,
    titleObject: selectTitleObject,
    unsubscribes,
    variants: selectABTestVariants,
})

export const recipientsOverviewSelectors = createStructuredSelector({
    id: getId,
    isABTest: selectIsABTest,
    isSendEmailTruncated: selectIsSendEmailTruncated,
    personCount,
    pressCount: selectPressCount,
    supporterCount,
    variantIds: selectABTestVariantIds,
    winnerId: selectABTestWinnerId,
})

export const variationCardConnection = createStructuredSelector({
    composeTool: (state, props) => selectVariantComposeTool(props.variantId)(state),
    htmlContent: (state, props) => variantHtmlContent(props.variantId)(state),
    subject: (state, props) => selectVariantSubject(props.variantId)(state),
    variantIdentifier: (state, props) => selectVariantIdentifier(props.variantId)(state),
    variantTableData: (state, props) => selectABTestVariantTableData(props.variantId)(state),
})

export const variantSendFromConnection = createStructuredSelector({
    activeDomain: (state, props) => selectVariantFromDomain(props.variantId)(state),
    assignedTeamMemberText: () => outboxFinalizeConstants.SEND_FROM_ASSIGNED_TEAM_MEMBER_TEXT,
    // isABTest: () => true,
    isABTest: selectIsABTest,
    objId: (state, props) => selectVariantFromUserId(props.variantId)(state),
    objEmailAddress: (state, props) => selectVariantFromAddressUsername(props.variantId)(state),
    objName: (state, props) => selectVariantFromName(props.variantId)(state),
    settingSelectDataCy: () => "from-email-settings",
    showAssignedTeamMemberText: (state, props) => selectVariantFromAssignments(props.variantId)(state),
    validFromDomains,
    variants: selectABTestVariants,
})

export const variantSendFromToggleSelectors = createStructuredSelector({
    isActive: (state, props) => selectVariantFromAssignments(props.variantId)(state),
    booleanField: () => "send_from_assignments",
    actionString: () => "Send From",
    helpText: () => outboxFinalizeConstants.SEND_FROM_ASSIGNED_TEAM_MEMBER_TEXT,
})

// we would normally choose the prefix name from the singular of the QDT,
// but the singular of bulkemail is "Outbox", which doesn't match the design
// system mocks for the bulkemail profile header
export const selectHeaderTitlePrefix = createSelector(selectIsABTest, (isABTest) => (isABTest ? "A/B Test" : "Email"))

// gets Campaigns
const getImmutableCampaigns = createBulkEmailSelector("campaigns", Immutable.fromJS([]))
// gets the uris
export const selectCampaignIds = createSelector(getImmutableCampaigns, (campaignUris) =>
    campaignUris.map((uri) => DjangIO.app.grassroots.campaign.models.Campaign.idFromResourceUri(uri)),
)

export const outboxCampaignSelectorConnection = createStructuredSelector({
    campaignIds: selectCampaignIds,
})

export const selectRenderPreviewButton = createSelector(selectIsABTest, (isABTest) => !isABTest)

export const outboxFinalActionsConnection = createStructuredSelector({
    isABTest: selectIsABTest,
    isScheduled,
    hideSendButtons,
    renderPreviewButton: selectRenderPreviewButton,
    shouldDisableSendButton: selectShouldDisableSendButton,
})

export const selectShouldForceUnsubscribeLink = createSelector(
    htmlContent,
    selectPressCount,
    isDefaultQuorumDomain,
    (htmlContent, pressCount, isDefaultQuorumDomain) =>
        htmlContent.includes("{{unsubscribe_link}}") ||
        pressCount >= outboxConstants.PRESS_CONTACT_UNSUBSCRIBE_THRESHOLD ||
        isDefaultQuorumDomain,
)

export const outboxUnsubscribeConnection = createStructuredSelector({
    isDefaultQuorumDomain,
    orgUnsubLinkText,
    isOverRecipientUnsubscribeLimit,
    shouldForceUnsubscribeLink: selectShouldForceUnsubscribeLink,
    unsubRadioChecked,
})

export const sendFromConnection = createStructuredSelector({
    activeDomain: fromDomain,
    assignedTeamMemberText: () => outboxFinalizeConstants.SEND_FROM_ASSIGNED_TEAM_MEMBER_TEXT,
    objId: fromUserId,
    objEmailAddress: fromAddress,
    objName: fromName,
    settingSelectDataCy: () => "from-email-settings",
    showAssignedTeamMemberText: sendFromAssignments,
    validFromDomains,
})

export const replyToConnection = createStructuredSelector({
    activeDomain: replyToDomain,
    objId: replyToUserId,
    objName: replyToName,
    objEmailAddress: replyToAddress,
    emailDomainDataCy: () => "reply-email-domain-input",
    emailAccountDataCy: () => "reply-email-account-input",
    personNameDataCy: () => "reply-person-name-input",
    settingSelectDataCy: () => "reply-to-settings",
    showAssignedTeamMemberText: replyToAssignments,
    assignedTeamMemberText: () => outboxFinalizeConstants.REPLY_TO_ASSIGNED_TEAM_MEMBER_TEXT,
})

export const CcConnection = createStructuredSelector({
    cc_field: () => "cc_rcpts",
    isCc: () => true,
    label: () => "Cc",
    recipients: ccRcpts,
})

export const BccConnection = createStructuredSelector({
    cc_field: () => "bcc_rcpts",
    isCc: () => false,
    label: () => "Bcc",
    recipients: bccRcpts,
})

export const composeConnection = createStructuredSelector({
    attachments: getAttachments,
    composeTool,
    htmlContent,
    jsonContent: getJsonContent,
    subject,
    aiSuggestion: getAiSuggestion,
})

export const sendConfirmationConnection = createStructuredSelector({
    fromAddress: makeFromEmail,
    fromName,
    isScheduled,
    replyToAddress: replyToFullEmailAddress,
    replyToName,
    scheduledSendMoment,
    subject,
})

export const abSendConfirmationConnection = createStructuredSelector({
    abTestDescription: selectABTestDescription,
    abTestVariable: selectABTestVariable,
    abTestVariants: selectABTestVariants,
    abTestWinCondition: selectABTestWinCondition,
    abTestDistribution: selectAdjustedABTestDistribution,
    fromAddress: makeFromEmail,
    fromName,
    isScheduled,
    replyToAddress: replyToFullEmailAddress,
    replyToName,
    scheduledSendMoment,
})

export const composeContainerConnection = createStructuredSelector({
    dbId: getId,
    defaultUnsubscribeSetting,
    bulkEmailId: getBulkEmailId,
    isABTest: selectIsABTest,
    isDraft,
    isFromCampaign,
    isLoading,
    isSending,
    isUpdating,
    rootBulkEmail: selectRootBulkEmail,
})

export const abTestWinnerConnection = createStructuredSelector({
    abTestMode: selectABTestMode,
    abTestWinCondition: selectABTestWinCondition,
    adjustedDistribution: selectAdjustedABTestDistribution,
    emailCount,
    rawDistribution: selectABTestDistribution,
})

export const abTestVariableConnection = createStructuredSelector({
    abTestVariable: selectABTestVariable,
})

export const abTestSchedulerConnection = createStructuredSelector({
    testMode: selectABTestMode,
    startTime: selectABTestStartTime,
    startTimeValidationError: selectABTestStartTimeValidationError,
    endTime: selectABTestEndTime,
    endTimeValidationError: selectABTestEndTimeValidationError,
})

export const abTestedBodyConnection = createStructuredSelector({
    placeholders: getPlaceholders,
    variants: selectABTestVariants,
})

export const abTestedBodyComposeConnection = createStructuredSelector({
    attachments: (state, props) => getAttachmentsABTest(props.variantId)(state, props.variantId),
    composeTool: (state, props) => selectVariantComposeTool(props.variantId)(state),
    htmlContent: (state, props) => selectVariantHtmlContent(props.variantId)(state),
    isABTestingBody: selectIsABTestingBody,
    jsonContent: (state, props) => getJsonContentABTest(props.variantId)(state),
})
